import { createRouter, createWebHistory } from 'vue-router'
import { LIURole, useLoggedInUserStore } from '@/stores/loggedInUser'
import { ModalType, launchModal } from '@/stores/modalControl'
import { parseRequestRole } from '@/types/requestTypes'
import { useDisplayConfigStore, Theme } from '@/stores/displayConfig'

// Enables typescript for the meta property in the routes
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuthentication: boolean
    requiredRoles: LIURole[]
    category: string
    manualTracking?: boolean
    backgroundTheme?: Theme
    kioskAllowed: boolean
  }
}

enum Category {
  General = 'General',
  Scheduling = 'Events',
  Requests = 'Requests',
  NoCategory = 'NoCategory',
  Settings = 'Settings',
  Communication = 'Communication',
  StaticContent = 'StaticContent'
}

// since this is the home page, don't lazy load it
import StaticContent from '../views/StaticContent.vue'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: StaticContent,
      meta: {
        requiresAuthentication: false,
        requiredRoles: [],
        backgroundTheme: Theme.Dark,
        category: Category.General,
        kioskAllowed: true
      },
      props: { name: 'home' }
    },
    {
      path: '/kiosk/:kioskMode?',
      name: 'kiosk',
      component: () => import('../Kiosk.vue'),
      meta: {
        requiresAuthentication: false,
        requiredRoles: [],
        category: Category.NoCategory,
        kioskAllowed: true
      },
      props: (route) => ({ kioskMode: !(route.params.kioskMode === 'false') })
    },
    {
      path: '/login',
      name: 'login',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/LoginView.vue'),
      meta: {
        requiresAuthentication: false,
        requiredRoles: [],
        category: Category.NoCategory,
        kioskAllowed: true
      },
      props: (route) => ({ redirectFrom: route.query.redirectFrom })
    },
    {
      path: '/admin/permissions',
      name: 'permission manager',
      component: () => import('../components/adminComponents/PermissionManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.Administrator],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/tools/linksmanager',
      name: 'links manager',
      component: () => import('../components/requests/LinksManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [], // anyone who logs in can access this page
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/admin/groups',
      name: 'adGroup manager',
      component: () => import('../components/adminComponents/AdGroupManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.Administrator],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/admin/locations',
      name: 'location manager',
      component: () => import('../components/adminComponents/LocationManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.Administrator],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/admin/systemSettings',
      name: 'system setting manager',
      component: () => import('../components/adminComponents/SystemSettingManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.Administrator],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/admin/siteSettings',
      name: 'site display manager',
      component: () => import('../components/adminComponents/SiteDisplayManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.Administrator],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/admin/eventLog',
      name: 'event log',
      component: () => import('../components/adminComponents/EventLogViewer.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.Administrator],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/admin/registeredsites',
      name: 'registered sites manager',
      component: () => import('../components/adminComponents/RegisteredSiteManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.Administrator],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/admin/classes',
      name: 'class manager',
      component: () => import('../components/adminComponents/CampusClassManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.ClassScheduler, LIURole.RequestsAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/admin/manageStore',
      name: 'store manager',
      component: () => import('../components/adminComponents/StoreManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.StoreAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/admin/balanceManager',
      name: 'balance manager',
      component: () => import('../components/adminComponents/BalanceManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.StoreAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/admin/prereqManager',
      name: 'prereq manager',
      component: () => import('../components/adminComponents/PrereqManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin, LIURole.RequestsAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/admin/debug',
      name: 'debug',
      component: () => import('../components/adminComponents/DebugPage.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.Administrator],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/admin/discount',
      name: 'discount manager',
      component: () => import('../components/manageRequests/DiscountGroupManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.Administrator, LIURole.RequestsAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/content/manage',
      name: 'content manager',
      component: () => import('../components/adminComponents/ContentManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.ContentAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/content/managecards/:title',
      name: 'manage cards',
      component: () => import('../components/adminComponents/ContentCardManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.ContentAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({ title: route.params.title })
    },
    {
      path: '/content/static/:id',
      name: 'static content',
      component: StaticContent,
      meta: {
        requiresAuthentication: false,
        requiredRoles: [],
        category: Category.StaticContent,
        backgroundTheme: Theme.None, // allow the page to set the background itself
        kioskAllowed: true
      },
      props: (route) => ({ name: route.params.id })
    },
    {
      path: '/scheduling/managecategories',
      name: 'manage categories',
      component: () => import('../components/manageSchedulingComponents/EventCategoryManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/scheduling/manageevents',
      name: 'manage events',
      component: () => import('../components/manageSchedulingComponents/EventManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/scheduling/manageevent/:id',
      name: 'manage event',
      component: () => import('../components/manageSchedulingComponents/EventManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({ eventIdProp: parseInt(route.params.id as string) })
    },
    {
      path: '/scheduling/scheduleinstance',
      name: 'event scheduler',
      component: () => import('../components/manageSchedulingComponents/EventScheduler.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin, LIURole.EventScheduler],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/scheduling/manageinstances',
      name: 'manage event instances',
      component: () => import('../components/manageSchedulingComponents/EventInstanceManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin, LIURole.EventScheduler, LIURole.EventProcessor],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/scheduling/instancestickets/:id',
      name: 'event instance tickets',
      component: () => import('../components/manageSchedulingComponents/EventInstanceTickets.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin, LIURole.EventScheduler, LIURole.EventProcessor],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({
        eventInstanceId: parseInt(
          typeof route.params.id === 'string' ? route.params.id : route.params.id[0]
        )
      })
    },
    {
      path: '/scheduling/editinstance/:id',
      name: 'edit event instance',
      component: () => import('../components/manageSchedulingComponents/EventScheduler.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin, LIURole.EventScheduler],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({
        editedEventInstId: parseInt(
          typeof route.params.id === 'string' ? route.params.id : route.params.id[0]
        )
      })
    },
    {
      path: '/scheduling/process',
      name: 'processing',
      component: () => import('../components/manageSchedulingComponents/ProcessEventNavigator.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin, LIURole.EventScheduler, LIURole.EventProcessor],
        category: Category.NoCategory,
        kioskAllowed: true
      }
    },
    {
      path: '/scheduling/processinstance/:id',
      name: 'process event instance',
      component: () => import('../components/manageSchedulingComponents/ProcessEventInstance.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin, LIURole.EventScheduler, LIURole.EventProcessor],
        category: Category.NoCategory,
        kioskAllowed: true
      },
      props: (route) => ({
        processInstanceId: parseInt(
          typeof route.params.id === 'string' ? route.params.id : route.params.id[0]
        )
      })
    },
    {
      path: '/scheduling/processevent/',
      name: 'process event',
      component: () =>
        import('../components/manageSchedulingComponents/ProcessUnscheduledEvent.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin, LIURole.EventScheduler, LIURole.EventProcessor],
        category: Category.NoCategory,
        kioskAllowed: true
      }
    },
    {
      path: '/scheduling/processevent/:id',
      name: 'process unscheduled event by id',
      component: () =>
        import('../components/manageSchedulingComponents/ProcessUnscheduledEvent.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin, LIURole.EventScheduler, LIURole.EventProcessor],
        category: Category.NoCategory,
        kioskAllowed: true
      },
      props: (route) => ({
        pEventId: parseInt(
          typeof route.params.id === 'string' ? route.params.id : route.params.id[0]
        )
      })
    },
    {
      path: '/scheduling/processbyclass',
      name: 'process by class',
      component: () => import('../components/manageSchedulingComponents/ProcessEventByClass.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin, LIURole.EventScheduler, LIURole.EventProcessor],
        category: Category.NoCategory,
        kioskAllowed: true
      }
    },
    {
      path: '/scheduling/records',
      name: 'event records',
      component: () => import('../components/manageSchedulingComponents/EventRecordTable.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.EventAdmin, LIURole.EventScheduler, LIURole.EventProcessor],
        category: Category.NoCategory,
        kioskAllowed: true
      },
      props: (route) => ({ eventId: parseInt(route.query.eventId as string) })
    },
    {
      path: '/calendar/:locationId',
      name: 'calendar',
      component: () => import('../components/scheduling/LocationCalendar.vue'),
      meta: {
        requiresAuthentication: false,
        requiredRoles: [],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({ locationId: route.params.locationId, ...route.query })
    },
    {
      path: '/calendar/:locationId/:locationId2',
      name: 'side by side calendars',
      component: () => import('../components/scheduling/SideBySideLocationCalendar.vue'),
      meta: {
        requiresAuthentication: false,
        requiredRoles: [],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({
        locationId: route.params.locationId,
        locationId2: route.params.locationId2
      })
    },
    {
      path: '/calendarmanager',
      name: 'calendar manager',
      component: () => import('../components/scheduling/CalendarManagerNavigator.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.CalendarAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/calendars/:selectedLocation?',
      name: 'calendars',
      component: () => import('../components/scheduling/MultiLocationCalendar.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [
          LIURole.EventAdmin,
          LIURole.EventScheduler,
          LIURole.CalendarAdmin,
          LIURole.EventProcessor
        ],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({ selectedLocation: route.params.selectedLocation })
    },
    {
      path: '/payment/:endpoint/:id',
      name: 'payment',
      component: () => import('../components/payment/PayflowPaymentPage.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({ endpoint: route.params.endpoint, id: route.params.id, ...route.query })
    },
    {
      path: '/payment/store',
      name: 'store',
      component: () => import('../components/payment/Store.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [],
        category: Category.General,
        kioskAllowed: false,
        manualTracking: true
      }
    },
    {
      path: '/receipt/:endpoint/:id',
      name: 'receipt',
      component: () => import('../components/payment/ReceiptPage.vue'),
      meta: {
        requiresAuthentication: false,
        requiredRoles: [],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({ endpoint: route.params.endpoint, id: route.params.id })
    },
    {
      path: '/transactionviewer',
      name: 'transaction viewer',
      component: () => import('../components/payment/TransactionViewer.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.TransactionReader],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/signup/upcomingworkshops',
      name: 'all upcoming events',
      component: () => import('../components/signup/Upcoming.vue'),
      meta: {
        requiresAuthentication: false,
        requiredRoles: [],
        category: Category.Scheduling,
        kioskAllowed: true
      },
      props: (route) => ({
        eventId: route.query.eventId,
        catId: route.query.categoryId,
        view: route.query.view
      })
    },
    {
      path: '/signup/details/:id',
      name: 'details',
      component: () => import('../components/signup/DetailedEventInstance.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [],
        category: Category.NoCategory,
        kioskAllowed: true
      },
      props: (route) => ({ eventInstanceId: route.params.id })
    },
    {
      path: '/signup/myevents',
      name: 'my events',
      component: () => import('../components/signup/MyEventsNavigator.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [],
        category: Category.Scheduling,
        kioskAllowed: true,
        manualTracking: true
      }
    },
    {
      path: '/signup/myevents/past',
      name: 'past events',
      component: () => import('../components/signup/MyPastEvents.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [],
        category: Category.Scheduling,
        kioskAllowed: true,
        manualTracking: false
      }
    },
    {
      path: '/signup/myevents/upcoming',
      name: 'my upcoming events',
      component: () => import('../components/signup/MyUpcomingEvents.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [],
        category: Category.Scheduling,
        kioskAllowed: true,
        manualTracking: false
      }
    },
    {
      path: '/myAccount/communicationSettings',
      name: 'communication settings',
      component: () => import('../components/myAccountComponents/UserCommunicationSettings.vue'),
      meta: {
        requiresAuthentication: false, // this needs to be accessible to outside for compliance reasons, page will need to handle the case where the user is not logged in
        requiredRoles: [],
        category: Category.Communication,
        kioskAllowed: true
      },
      props: (route) => ({ endpoint: route.params.endpoint, id: route.params.id })
    },
    {
      path: '/myAccount/MyMessages',
      name: 'My Messages',
      component: () => import('../components/myAccountComponents/MyMessages.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [],
        category: Category.Communication,
        kioskAllowed: true
      },
      props: (route) => ({ endpoint: route.params.endpoint, id: route.params.id })
    },
    {
      path: '/requests/manage/',
      name: 'manage groups',
      component: () => import('../components/manageRequests/FormGroupManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.RequestsAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/requests/navigate',
      name: 'request navigator',
      component: () => import('../components/requests/FormNavigator.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [],
        category: Category.Requests,
        kioskAllowed: false
      },
      props: (route) => ({ groupId: route.query.groupId })
    },
    {
      path: '/requests/view/:formName',
      name: 'request form',
      component: () => import('../views/RequestView.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [],
        category: Category.Requests,
        kioskAllowed: false
      },
      props: (route) => ({
        formName: route.params.formName,
        groupName: null
      })
    },
    {
      path: '/requests/view/:groupName/:formName',
      name: 'request form with group',
      component: () => import('../views/RequestView.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [],
        category: Category.Requests,
        kioskAllowed: false
      },
      props: (route) => ({
        formName: route.params.formName,
        groupName: route.params.groupName
      })
    },
    {
      path: '/requests/resubmit/:formName/:submissionId',
      name: 'resubmit form',
      component: () => import('../views/RequestView.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({
        formName: route.params.formName,
        submissionId: parseInt(route.params.submissionId as string)
      })
    },
    {
      path: '/requests/viewsubmissions',
      name: 'request submissions',
      component: () => import('../views/RequestSubmissionView.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.RequestsProcessor],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({
        pFormGroup: route.query.formGroupId,
        pForm: route.query.formId,
        pStatus: route.query.status,
        pUserId: route.query.submittedById,
        pAssignedToId: route.query.assignedToId,
        pShowDone: route.query.showDone,
        pSortBy: route.query.sortBy,
        pColumns: route.query.columns,
        pSortOrder: route.query.sortOrder,
        pPage: route.query.page,
        pItemsPerPage: route.query.itemsPerPage
      })
    },
    {
      path: '/requests/viewsubmission/:id/:role',
      name: 'request submission',
      component: () => import('../components/requests/SubmissionDisplay.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [], // allow backend to handle this logic
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({
        submissionId: route.params.id,
        roleOverride: parseRequestRole(route.params.role as string)
      })
    },
    {
      path: '/requests/myrequests',
      name: 'my requests',
      component: () => import('../views/MyRequestSubmissionView.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [],
        category: Category.Requests,
        kioskAllowed: false
      }
    },
    {
      path: '/centers/manage',
      name: 'center manager',
      component: () => import('../components/kiosk/CenterManager.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.Administrator],
        category: Category.NoCategory,
        kioskAllowed: false
      }
    },
    {
      path: '/docs',
      name: 'Bookstack Search',
      component: () => import('../components/BookstackAdvancedSearch.vue'),
      meta: {
        requiresAuthentication: false,
        requiredRoles: [],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({ initialSearch: route.query.search })
    },
    {
      path: '/tapStation/:centerId',
      name: 'tap station',
      component: () => import('../components/kiosk/TapStation.vue'),
      meta: {
        requiresAuthentication: false,
        requiredRoles: [],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({ centerId: route.params.centerId, ...route.query })
    },
    {
      path: '/centerUsers/:centerId',
      name: 'center users',
      component: () => import('../components/kiosk/CenterUsers.vue'),
      meta: {
        requiresAuthentication: true,
        requiredRoles: [LIURole.Administrator, LIURole.EventAdmin],
        category: Category.NoCategory,
        kioskAllowed: false
      },
      props: (route) => ({ centerId: route.params.centerId })
    }
  ]
})

// Check if the user is logged in and has the required role
router.beforeEach(async (to, from) => {
  if (!to.meta.requiresAuthentication) {
    return true
  }

  const liu = useLoggedInUserStore()

  if (!liu.isLoggedIn) {
    await liu.checkForCookie()
  }

  if (to.meta.requiresAuthentication && !liu.isLoggedIn) {
    launchModal({
      message: 'You need to be logged in to access this page',
      header: 'Not Logged In',
      confirmButtonText: 'Ok',
      type: ModalType.Information
    })
    return '/login?redirectFrom=' + to.fullPath
  }

  // perform any kind of forced redirect
  if (from.name == 'receipt' && to.name == 'payment') {
    return '/'
  }

  const overlap = to.meta.requiredRoles.filter((role) => liu.roles.includes(role))
  if (
    !liu.roles.includes(LIURole.Administrator) &&
    to.meta.requiredRoles.length > 0 &&
    overlap.length === 0
  ) {
    launchModal({
      message: `You are not authorized to access the ${String(to.name)} page`,
      header: 'Not Allowed',
      confirmButtonText: 'Ok',
      type: ModalType.Information
    })
    return from
  }
})

// for controling the background color
router.beforeEach(async (to, from) => {
  if (to.meta.backgroundTheme === Theme.Dark) {
    document.documentElement.style.setProperty('--color-background', '#000000')
  } else {
    document.documentElement.style.setProperty('--color-background', '#f4f4f4')
    //document.documentElement.style.setProperty('--image-background', 'none')
  }
  if (!from || from.meta.backgroundTheme !== to.meta.backgroundTheme) {
    //document.documentElement.style.setProperty('--image-background', 'none')
  }
})

router.beforeEach(async (to, from) => {
  // for changing page title
  document.title = toTitleCase(to.name?.toString()) ?? 'Conductor'

  // for protecting kiosk mode
  const displayConfig = useDisplayConfigStore()
  if (!to.meta.kioskAllowed && displayConfig.isKiosk) {
    launchModal({
      message: 'This page is not available in kiosk mode',
      header: 'Not Allowed',
      confirmButtonText: 'Ok',
      type: ModalType.Information
    })
    return from
  }
})

function toTitleCase(str: string | undefined) {
  return str?.replace(
    /\w\S*/g,
    (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
  )
}

router.afterEach(async (to, from) => {
  if (to.fullPath !== router.currentRoute.value.fullPath) {
    // Navigation was cancelled, skip logic
    return
  }

  // display config for theme
  const displayConfig = useDisplayConfigStore()
  displayConfig.setTheme(to.meta.backgroundTheme || Theme.Light)

  // - - - - for tracking page views - - - -
  // first, deal with manual tracking
  if (to.meta.manualTracking !== undefined) {
    if (!to.meta.manualTracking) {
      return
    }
  }
  // Only send if it doesn't require a special role and it's not moving within a page
  else if (
    to.meta.requiredRoles.length > 0 ||
    to.name === from.name ||
    to.meta.category === Category.NoCategory
  ) {
    return
  }

  // special case formatting
  // first, for each form, break them into separate page names
  var name = to.name as string
  if (name == 'request form') {
    name = to.path.split('/')[3]
    if (!name.endsWith(' request') && !name.endsWith(' form')) {
      name += ' request'
    }
    if (name.includes('_')) {
      name = name.replaceAll('_', ' ')
    }
  } else if (name == 'request form with group') {
    name = to.path.split('/')[4]
    if (!name.endsWith(' request') && !name.endsWith(' form')) {
      name += ' request'
    }
    if (name.includes('_')) {
      name = name.replaceAll('_', ' ')
    }
  }
  name = name.replaceAll('%20', ' ').trim()

  try {
    const response = await fetch('/api/content/TrackPageView', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        pageName: name,
        path: to.path,
        category: to.meta.category
      })
    })
    if (!response.ok) {
      console.error('Failed to track page view')
    }
  } catch (e) {
    console.error('Failed to track page view')
  }
})

export default router
